<template>
  <div class="login-form">
    <div class="login-header">
      <h1 class="login-title" :style="getPrimaryColor">LogIn</h1>
      <div class="nav-link text-right" v-if="isSysUserRegistrationEnabled">
        Don't have an account ?
        <router-link to="/signup" class="fw-bold" :style="getPrimaryColor">SignUp!</router-link>
      </div>
    </div>
    <div class="divider">
      <span class="line"></span>
      <span class="line"></span>
    </div>
    <el-form
      :model="signin"
      ref="signin"
      class="log-form"
      @keyup.enter.native="submitForm('signin')"
    >
      <el-form-item prop="email">
        Email Address
        <span class="text-danger">*</span>
        <el-input
          v-model="signin.email"
          placeholder="Ex: johnwesley@abc.com"
          autocomplete="off"
        ></el-input>
        <p class="error" v-if="getErrors && getErrors.email">
          {{ getErrors.email }}
        </p>
      </el-form-item>
      <el-form-item class="mb-1" prop="password">
        Password
        <span class="text-danger">*</span>
        <div class="password-input-wrapper">
          <el-input
            :type="passwordType"
            placeholder="Enter Password"
            v-model="signin.password"
            autocomplete="off"
          ></el-input>
          <span
            class="password-toggle"
            :style="getPrimaryColor"
            @click="passwordType = passwordType === 'password' ? 'text' : 'password'"
          >
            {{ passwordType === 'password' ? 'SHOW' : 'HIDE' }}
          </span>
        </div>
        <p class="error" v-if="getErrors && getErrors.password">
          {{ getErrors.password }}
        </p>
      </el-form-item>
      <el-form-item class="mt-1">
        <div class="forgot-login-row space-between">
          <div class="nav-link text-right forgot-password-link" @click="goToForgotPassword">
            Forgot Password?
          </div>
          <el-button
            type="primary"
            size="medium"
            round
            @click="submitForm('signin')"
            :loading="submitLoading"
            :style="getButtonStyles"
          >
            LogIn
          </el-button>
        </div>
      </el-form-item>
      <p class="error" v-if="getErrors && getErrors.critical_error">
        {{ getErrors.critical_error }}
        <span v-if="getErrorCode === 'USER_NOT_FOUND'">
          Please use a different account or
          <router-link to="/signup" class="fw-medium">Sign Up</router-link>
          for a new account.
        </span>
        <span v-if="getErrorCode === 'HOLD_WORKSPACE'">
          <span class="fw-medium" style="color: #1989fa" @click="resendVerificationMail">
            Click here to send activation link
          </span>
        </span>
      </p>

      <div class="divider">
        <span class="line"></span>
        <span class="divider-text">OR</span>
        <span class="line"></span>
      </div>
      <div class="forgot-login-row">
        <div
          class="nav-link text-right forgot-password-link"
          @click="goToSSOPage"
          v-if="isEnabledSSO"
        >
          Sign In with SSO
        </div>
        <div class="nav-link text-right forgot-password-link" @click="goToTenant">
          {{ getTenantName }} Login
        </div>
      </div>
    </el-form>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { clearDB } from '@/repo/appSettingRepo.js';
  import Swal from 'sweetalert2';
  import DefaultSEOData from '@/config/seo';
  import CustomizationLayer from '@/mixins/CustomizationLayer';
  import app from '@/config/app';
  export default {
    name: 'SignIn',
    mixins: [CustomizationLayer],
    data() {
      return {
        pageData: {
          title: '',
          description: '',
          keywords: '',
          image: '',
          favicon: '',
        },
        submitLoading: false,
        passwordType: 'password',
        signin: {
          email: '',
          phone: '',
          password: '',
          remember_me: '',
        },
        rules: {
          email: [
            {
              required: true,
              message: 'Email is required',
              trigger: 'blur',
            },
            {
              type: 'email',
              message: 'Invalid Email',
              trigger: 'blur',
            },
          ],
          password: [
            {
              required: true,
              message: 'Password is required',
              trigger: 'blur',
            },
          ],
        },
        applicationSettings: app,
      };
    },
    metaInfo() {
      const meta = { ...DefaultSEOData, ...this.pageData };
      return {
        title: meta.title || 'Default Title',
        meta: [
          // Basic SEO
          { name: 'description', content: meta.description || '' },
          { name: 'keywords', content: meta.keywords || '' },
          { name: 'robots', content: 'index,follow' },

          // OpenGraph (for social sharing)
          { property: 'og:title', content: meta.title || '' },
          { property: 'og:description', content: meta.description || '' },
          { property: 'og:image', content: meta.image || '' },
          { property: 'og:type', content: 'website' },

          // Twitter Cards
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: meta.title || '' },
          { name: 'twitter:description', content: meta.description || '' },
          { name: 'twitter:image', content: meta.image || '' },
        ],
        link: [
          // Favicon and icons
          { rel: 'icon', type: 'image/x-icon', href: meta.favicon || '/favicon.ico' },
          { rel: 'icon', type: 'image/png', href: meta.favicon || '/favicon.png' },
          { rel: 'apple-touch-icon', href: meta.favicon || '/apple-touch-icon.png' },
        ],
      };
    },
    mounted() {
      this.$store.dispatch('auth/reset');
      this.$store.commit('company/resetCompaniesByEmail');
    },
    computed: {
      ...mapGetters('errors', ['getErrors', 'errorResponse']),
      ...mapGetters('auth', [
        'getErrorMessage',
        'getOtpVerifiedStatus',
        'getAuthenticationStatus',
        'getAccountNotVerified',
        'getAuthenticatedUser',
        'getErrorData',
        'getResendVerificationEmail',
      ]),
    },
    created() {
      this.$store.commit('errors/setErrors', null, { root: true });
      setTimeout(() => {
        this.pageData = this.getPageTagsForSEO('LOGIN');
      }, 1000);
    },
    methods: {
      goToSSOPage(email = null) {
        if (this.signin.email) {
          email = this.signin.email;
        }
        const themeConfigurations = {
          primary: this.applicationSettings.SSO_THEME_PRIMARY_COLOR,
          secondary: this.applicationSettings.SSO_THEME_SECONDARY_COLOR,
          bg: this.applicationSettings.SSO_THEME_BG_COLOR,
          bg_image: this.applicationSettings.SSO_THEME_BG_IMAGE,
          title: this.applicationSettings.SSO_THEME_TITLE,
          favicon: this.applicationSettings.SSO_THEME_FAVICON,
        };

        let url = '/sso-signin';
        if (email) {
          this.$router.push({ path: url, query: { email, ...themeConfigurations } });
        } else {
          this.$router.push({ path: url, query: { ...themeConfigurations } });
        }
      },
      goToTenant() {
        const redirectUrl = this.$route.query?.redirect_url;

        this.$router.push({
          path: '/signin/tenant',
          query: redirectUrl ? { redirect_url: redirectUrl } : {},
        });
      },
      async resendVerificationMail() {
        try {
          this.loading = true;
          await this.$store.dispatch('auth/resendInviteEmailToUser', {
            email: this.signin.email.toLowerCase(),
            company: this.getErrorData,
          });

          if (this.getResendVerificationEmail) {
            this.$notify.success({
              title: 'Success',
              message: 'Verification mail sent to your email',
            });
            this.signin = {
              email: '',
              phone: '',
              password: '',
              remember_me: '',
            };
            this.$store.commit('errors/reset', null, { root: true });
            this.$store.commit('auth/setErrorCode', '', { root: true });
            this.$store.commit('auth/setErrorData', '', { root: true });
          }
          this.loading = false;
        } catch (err) {
          this.loading = false;
          this.$notify.error({
            title: 'Error',
            message: 'Error at sending verification mail',
          });
        }
      },

      submitForm(formName) {
        this.signin.email = this.signin.email.trim();
        this.$refs[formName].validate((valid) => {
          if (valid) {
            this.signIn();
          } else {
            return false;
          }
        });
      },

      resetForm(formName) {
        this.$refs[formName].resetFields();
      },
      async fetchWorkspaceInfo() {
        this.$router.push({ path: '/mfa', query: this.$route.query });
      },
      async signIn() {
        try {
          this.submitLoading = true;
          let params = {
            email: this.signin.email.toLowerCase(),
            password: this.signin.password,
          };
          await this.$store.dispatch('auth/signin', params);
          if (this.getAccountNotVerified) {
            this.submitLoading = false;
          } else if (this.getAuthenticationStatus) {
            this.$store.commit('errors/reset', null, { root: true });
            this.$store.commit('auth/setErrorCode', '', { root: true });
            this.$store.commit('auth/setErrorData', '', { root: true });
            await this.fetchWorkspaceInfo();
          } else {
            this.submitLoading = false;
          }
        } catch (err) {
          this.submitLoading = false;
          console.log('signIn', err);
        }
      },
      alert() {
        Swal.fire({
          title: 'OOPs',
          text: 'Failed to login into your account....',
          icon: 'error',
          showCancelButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Ok',
        }).then(async (result) => {
          if (result.isConfirmed) {
            await clearDB(true);
          }
        });
      },
      goToForgotPassword() {
        this.$router.push({ path: '/forgot-password' });
      },
    },
  };
</script>
<style>
  .swal2-container.swal2-center > .swal2-popup {
    padding: 1% !important;
  }
</style>

<style scoped>
  a {
    text-decoration: none !important;
    color: #196392 !important;
  }

  a:hover {
    text-decoration: underline !important;
    /* Optional: underline on hover */
    color: #196392 !important;
  }

  /* Flex container to space out the links and the divider */
  .forgot-login-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    flex-wrap: wrap;
  }

  .forgot-password-link {
    /* font-size: 14px;
  color: #000000; */
    text-decoration: none;
    /* font-weight: bold; */
    cursor: pointer;
    margin: 4px 0;
  }

  .forgot-password-link:hover {
    text-decoration: underline;
  }

  /* Divider with lines and 'OR' in the middle */
  .divider {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: center;
    /* margin: 0 20px; */
  }

  .line {
    flex: 1;
    border-top: 1px solid #ddd;
    /* light gray line */
  }

  .divider-text {
    margin: 0 10px;
    color: #aaa;
    font-size: 16px;
    font-weight: bold;
  }

  /* Flex container for title and link */
  .login-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .login-title {
    font-size: 24px;
    margin: 0;
    color: #196392;
  }

  .center-link {
    /* width: 100%; */
    text-align: center;
  }

  .password-input-wrapper {
    position: relative;
  }

  .password-toggle {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #409eff;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    user-select: none;
  }

  .login-form {
    border: 1px solid #cfcfcf;
    border-radius: 2em;

    width: 100%;
    max-width: 550px;
    padding: 2rem;
    /* border-radius: 8px; */
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
  }

  .login-form h2 {
    margin-bottom: 1.5rem;
    color: #333;
    text-align: center;
  }

  .login-form input {
    width: 100%;
    padding: 0.75rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;
  }

  /* FULL RESPONSIVE FOR MOBILE & TABLET */
  @media (max-width: 1024px) {
    .login-form {
      border: 1px solid #cfcfcf;
      border-radius: 2em;
      /* max-width: 100%; */
      padding: 1.5rem;
      margin: 0 auto;
      box-shadow: none;
      /* border-radius: 0; */
    }

    .login-form h2 {
      font-size: 1.5rem;
    }

    .password-toggle {
      right: 10px;
      font-size: 12px;
    }
  }
</style>
